import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { discoveredPageQuery, discoveredTableQuery, type DiscoveredFilter, type DiscoveredRow } from '../api/inventoree'
import { DataTable, useTableState } from '../ui/DataTable'
import { Content } from '../ui/Shell'
import { barBreakdownHtml } from './discovered/bars'
import { discoveredColumns } from './discovered/columns'
import { discoveredDetailsHtml } from './discovered/details'
import { DiscoveredMultiActions } from './DiscoveredMultiActions'
import { useDiscoveredRowActions } from './DiscoveredRowActions'
import { discoveredPresenceQuery } from '../api/discoveredActions'
import { fromNow, stamp } from './format'

/**
 * Inventoree :: Live Devices - macheta inventoree/live/index.php +
 * DevicesModel + barBreakdown.
 *
 * Bifa pe un rand deschide sertarul din dreapta, ca in portal: acolo
 * `MultipleRowsSelect` ridica `multiRowsAllChecked`, iar
 * `MultiActionsSidebar.openSidebar` cheama `DiscoveryMultiActions`
 * (`public/js/views/inventoree/live/index-1.js`). Aici numarul randurilor
 * bifate se tine in ecran si se da mai departe sertarului.
 *
 * Meniul de pe rand (deploy / check before deploy / add as unmanaged / check
 * alive / delete) e in `DiscoveredRowActions`; "Watch from outside" ramane in
 * portalul vechi pana ajunge in v4 monitorizarea din exterior (Proactivitee).
 */

const TABLE_ID = 'discovery-table'

const PAGE_CSS = `
#page-content { padding: 0; }
#content .dataTables_filter { margin-bottom: 8px; }
#content > .row { margin-left: 0; margin-right: 0; }
#content > .row > [class*="col-"] { padding-left: 0; padding-right: 0; }
.nd-bd { padding: 2px 0 0; }
.nd-bd-row { display: flex; align-items: center; gap: 8px; padding: 3px 0; }
.nd-bd-label { flex: 0 0 96px; font-size: 11px; color: #5b6672; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.nd-bd-track { flex: 1 1 auto; height: 10px; background: #eef1f5; border-radius: 4px; }
.nd-bd-fill { display: block; height: 10px; background: #2a78d6; border-radius: 4px; }
.nd-bd-row.is-unknown .nd-bd-fill { background: #6b7683; }
.nd-bd-row.is-unknown .nd-bd-label { color: #6b7683; font-style: italic; }
.nd-bd-value { flex: 0 0 34px; text-align: right; font-size: 12px; font-weight: 600; color: #2f3b46; font-variant-numeric: tabular-nums; }
.nd-bd-total { margin-top: 6px; font-size: 11px; color: #9aa4b1; }
.nd-bd-empty { font-size: 12px; color: #9aa4b1; padding: 10px 0; }
.nd-bd-title { font-size: 11px; text-transform: uppercase; letter-spacing: .04em; color: #9aa4b1; margin-bottom: 4px; }
.dd-panel { background: #f7f9fb; border-top: 1px solid #e6eaee; padding: 12px 16px 14px; }
.dd-head { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; margin-bottom: 10px; }
.dd-title { font-size: 14px; font-weight: 600; color: #2f3b46; }
.dd-chip { font-size: 11px; font-weight: 600; padding: 1px 8px; border-radius: 9px; background: #eef4fb; color: #3b6ea5; }
.dd-chip-quiet { background: #eef1f5; color: #6b7683; font-weight: 500; }
.dd-groups { display: flex; flex-wrap: wrap; gap: 10px; align-items: flex-start; }
.dd-group { flex: 1 1 240px; min-width: 220px; background: #fff; border: 1px solid #e6eaee; border-radius: 4px; padding: 9px 12px 10px; }
.dd-group-wide { flex: 1 1 100%; }
.dd-group h6 { margin: 0 0 6px; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; color: #9aa4b1; }
.dd-fact { display: flex; gap: 10px; padding: 2px 0; font-size: 12px; }
.dd-k { flex: 0 0 108px; color: #8a939c; }
.dd-v { flex: 1 1 auto; color: #3c4652; word-break: break-word; }
.dd-empty { margin: 0; font-size: 12px; color: #8a939c; }
.dd-ports { display: flex; flex-wrap: wrap; gap: 4px; }
.dd-port { font-size: 11px; font-family: monospace; padding: 1px 7px; border-radius: 3px; background: #eef1f5; color: #3c4652; }
.dd-empty-bad { color: #a0362a; }
.dd-loading { padding: 8px 0 0; font-size: 12px; color: #9aa4b1; }
.dd-runs { display: flex; flex-wrap: wrap; gap: 4px 16px; }
.dd-run { display: flex; gap: 6px; align-items: baseline; font-size: 12px; }
.dd-run-when { color: #3c4652; }
.dd-run-meta { color: #9aa4b1; font-size: 11px; }
`

/**
 * Firul de prezenta - cand a fost vazut de fiecare scanare - se cere abia la
 * desfacere, pentru randul asta: o interogare pe rand, numai cand cineva se uita.
 */
function Presence({ id }: { id: number }) {
  const presence = useQuery(discoveredPresenceQuery(id))
  if (presence.isPending) {
    return (
      <div className="dd-presence">
        <div className="dd-loading">
          <i className="fa fa-spinner fa-spin"></i> Reading the scan runs…
        </div>
      </div>
    )
  }
  if (presence.isError) {
    return (
      <section className="dd-group dd-group-wide">
        <p className="dd-empty dd-empty-bad">The scan runs could not be read.</p>
      </section>
    )
  }
  const list = presence.data?.timeline ?? []
  if (list.length === 0) {
    /* "Nu l-a vazut nicio scanare" NU inseamna ca aparatul e nou: rularile se
       tin de cand releul a inceput sa le raporteze. */
    return (
      <section className="dd-group dd-group-wide">
        <h6>Presence</h6>
        <p className="dd-empty">
          No recorded scan run has seen this device. Runs are only kept from the day the relay started reporting them,
          so a device found before that has none.
        </p>
      </section>
    )
  }
  const now = new Date()
  return (
    <section className="dd-group dd-group-wide">
      <h6>
        Presence ({list.length} scan run{list.length === 1 ? '' : 's'})
      </h6>
      <div className="dd-runs">
        {list.map((ev) => {
          const when = stamp(ev.seen_at)
          return (
            <div className="dd-run" key={ev.run_id}>
              <span className="dd-run-when" title={ev.seen_at ?? ''}>
                {when ? fromNow(when, now) : '—'}
              </span>
              <span className="dd-run-meta">
                run #{ev.run_id} · matched by {ev.matched_by}
              </span>
            </div>
          )
        })}
      </div>
    </section>
  )
}

function Details({ row }: { row: DiscoveredRow }) {
  return (
    <div>
      <div dangerouslySetInnerHTML={{ __html: discoveredDetailsHtml(row) }} />
      <div className="dd-panel" style={{ paddingTop: 0 }}>
        <div className="dd-groups">
          <Presence id={Number(row.ID)} />
        </div>
      </div>
    </div>
  )
}

export function DiscoveredPage() {
  const page = useQuery(discoveredPageQuery())
  const [filter, setFilter] = useState<DiscoveredFilter>('live_devices')
  const [selected, setSelected] = useState<Set<number>>(() => new Set())
  const columns = useMemo(() => discoveredColumns(), [])
  /* Ordinea implicita: Deploy ready, crescator - "pe care pot instala" e
     intrebarea cu care se intra pe ecran (DISCOVERY_ORDER_VERSION 2 in portal). */
  /* `?search=` vine din ADM Exposure: un aparat gasit numai de scanare n-are
     fisa a lui, deci legatura de pe numele lui duce aici, cautat dupa adresa. */
  const [state, setState] = useTableState(TABLE_ID, {
    start: 0,
    length: 25,
    order: { column: 5, dir: 'asc' },
    search: (new URLSearchParams(window.location.search).get('search') ?? '').trim().slice(0, 100),
  })
  const orderColumn = columns.find((c) => c.index === state.order.column) ?? columns[5]

  const table = useQuery({
    ...discoveredTableQuery({
      draw: 1,
      start: state.start,
      length: state.length,
      orderColumn: orderColumn.index,
      orderName: orderColumn.name ?? '',
      orderDir: state.order.dir,
      search: state.search,
      filter,
    }),
    placeholderData: keepPreviousData,
  })

  const typesHtml = barBreakdownHtml((page.data?.types ?? []) as unknown as Record<string, unknown>[], {
    labelField: 'device_type',
    valueField: 'devices',
    empty: 'Nothing discovered yet.',
  })
  const osHtml = barBreakdownHtml((page.data?.os ?? []) as unknown as Record<string, unknown>[], {
    labelField: 'os_family',
    valueField: 'devices',
    empty: 'Nothing discovered yet.',
  })

  const rows = (table.data?.data ?? []) as DiscoveredRow[]

  /* Dupa o actiune: lista se reincarca, iar bifele pleaca - randurile sterse
     nu mai exista, si cele ramase si-au schimbat starea. */
  const afterAction = () => {
    setSelected(new Set())
    void table.refetch()
    void page.refetch()
  }

  /* Meniul de pe rand si butoanele de pe toata lista. */
  const rowActions = useDiscoveredRowActions(rows, page.data, afterAction)

  /**
   * Bifa, ca pe celelalte liste: se asculta clicul pe casuta din celula, nu se
   * leaga un `onChange` pe fiecare - celulele vin ca HTML si se rescriu la
   * fiecare desenare. Cea din cap (`allDevices`) le bifeaza pe toate de pe
   * pagina.
   */
  const onTableClick = (e: React.MouseEvent<HTMLDivElement>) => {
    rowActions.onClick(e)
    const target = e.target as HTMLElement
    if (target instanceof HTMLInputElement && target.classList.contains('oneDevice')) {
      const id = Number(target.value)
      setSelected((s) => {
        const next = new Set(s)
        if (target.checked) next.add(id)
        else next.delete(id)
        return next
      })
    } else if (target instanceof HTMLInputElement && target.classList.contains('allDevices')) {
      setSelected(target.checked ? new Set(rows.map((r) => Number(r.ID))) : new Set())
    }
  }

  return (
    <Content>
      <style>{PAGE_CSS}</style>
      {rowActions.modals}
      <div className="row" onClick={onTableClick}>
          <div className="col-lg-9">
            <DataTable
              id={TABLE_ID}
              columnTools
              columns={columns}
              rows={rows.map((r) => ({ ...r, __checked: selected.has(Number(r.ID)) })) as unknown as Record<string, unknown>[]}
              total={table.data?.recordsTotal ?? 0}
              fetching={table.isFetching}
              error={table.error ? table.error.message : null}
              onRetry={() => void table.refetch()}
              state={state}
              onState={setState}
              rowId={(row) => String(row.DT_RowId)}
              headerButtons={rowActions.listButtons}
              headerView={
                <div className="tk-lift-to-header" style={{ display: 'inline-block', textAlign: 'left' }}>
                  <select
                    className="form-control input-sm"
                    id="discoveredFilter"
                    title="What the list shows"
                    style={{ minWidth: 200 }}
                    value={filter}
                    onChange={(e) => {
                      setFilter(e.target.value === 'can_deploy' ? 'can_deploy' : 'live_devices')
                      setState({ start: 0 })
                    }}
                  >
                    <option value="live_devices">Everything discovered</option>
                    <option value="can_deploy">Agent Ready</option>
                  </select>
                </div>
              }
              footerButtons={{ copy: true, colvis: true, refresh: () => void table.refetch() }}
              details={(row) => <Details row={row as unknown as DiscoveredRow} />}
            />
          </div>
          <div className="col-lg-3">
            <div className="panel panel-default">
              <div className="panel-heading">Discovered</div>
              <div className="panel-body">
                <div className="nd-bd-title">By type</div>
                <div id="discoveredStatistics" className="chart-container" dangerouslySetInnerHTML={{ __html: typesHtml }} />
                <div className="nd-bd-title" style={{ marginTop: 14 }}>
                  By operating system
                </div>
                <div id="discoveredOsStatistics" className="chart-container" dangerouslySetInnerHTML={{ __html: osHtml }} />
              </div>
            </div>
            <div className="panel panel-default">
              <div className="panel-heading">History</div>
              <div className="panel-body">
                <div style={{ height: 260 }} id="discoveredHistory" className="chart-container">
                  {(page.data?.history ?? []).length === 0 ? (
                    <div className="nd-bd-empty">Nothing discovered yet.</div>
                  ) : (
                    <HistoryBars rows={page.data?.history ?? []} />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      {/* Zona din dreapta, cat timp e bifat cel putin un rand. */}
      <DiscoveredMultiActions ids={[...selected].sort((a, b) => a - b)} onDone={afterAction} />
    </Content>
  )
}

function HistoryBars({ rows }: { rows: { saw: string; devices: number }[] }) {
  const max = Math.max(1, ...rows.map((r) => r.devices))
  return (
    <div className="nd-bd" role="img" aria-label="Discovered history">
      {rows.map((r) => (
        <div className="nd-bd-row" key={r.saw} title={`${r.saw}: ${r.devices}`}>
          <span className="nd-bd-label">{r.saw}</span>
          <span className="nd-bd-track">
            <span className="nd-bd-fill" style={{ width: `${Math.max(2, Math.round((r.devices / max) * 100))}%`, background: '#4DB6AC' }} />
          </span>
          <span className="nd-bd-value">{r.devices}</span>
        </div>
      ))}
    </div>
  )
}
